import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/Card.tsx';
import { ColumnLabel } from '@/components/ui/Title.tsx';

/** Karta w centrum uwagi stoi w bursztynie, pozostałe na białym tle. */
export type InfoCardTone = 'plain' | 'accent';

interface InfoCardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: InfoCardTone;
}

/**
 * Karta z listą danych — stan rekordu, podsumowania, panele boczne.
 * `overflow-hidden` przycina nagłówek i wiersze do zaokrąglenia karty, bo
 * kreski wewnątrz idą na całą szerokość.
 */
const InfoCard = ({ tone = 'plain', className, ...props }: InfoCardProps) => (
  <Card
    highlighted={tone === 'accent'}
    className={cn(
      'overflow-hidden',
      tone === 'accent' && 'bg-accent-100',
      className,
    )}
    {...props}
  />
);

interface InfoCardHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Tytuł karty; szkielety podstawiają tu `<Skeleton />`. */
  title: React.ReactNode;
  /** Dopisek w linii tytułu, np. licznik pozycji. */
  aside?: React.ReactNode;
}

/**
 * Nagłówek karty — wersalikowa etykieta, jak nagłówki kolumn. `children`
 * schodzi pod tytuł, w obrębie kreski nagłówka.
 */
const InfoCardHeader = ({
  title,
  aside,
  className,
  children,
  ...props
}: InfoCardHeaderProps) => (
  <div
    className={cn('border-b border-neutral-100 px-5 pb-3.5 pt-4', className)}
    {...props}
  >
    <div className="flex items-baseline gap-2">
      <ColumnLabel>{title}</ColumnLabel>
      {aside}
    </div>
    {children}
  </div>
);

/** Treść karty pod nagłówkiem — pionowa lista wierszy w marginesach karty. */
const InfoCardBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col gap-[11px] px-5 pb-4 pt-3.5', className)}
    {...props}
  />
);

/**
 * Kształt wiersza. `spread` rozpycha etykietę i wartość na końce — czyta się
 * dobrze w wąskiej karcie panelu. `columns` stawia wartość w stałej kolumnie
 * 120 px od etykiety, bo w szerokiej sekcji wartość odepchnięta na drugi koniec
 * gubi związek z opisem. Kolumny zostają kolumnami także na telefonie: para
 * „etykieta → wartość” złożona pionowo to dwie linie zamiast jednej i dokładnie
 * ten rozsypany obraz, na który skarżyli się użytkownicy modali finansowych.
 */
export type InfoCardRowLayout = 'spread' | 'columns';

interface InfoCardRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Etykieta z lewej; szkielety podstawiają tu `<Skeleton />`. */
  label: React.ReactNode;
  layout?: InfoCardRowLayout;
  /** Wiersz podsumowania — kreska odcina go od listy powyżej. */
  divided?: boolean;
  /**
   * Akcja tego jednego wiersza, dosunięta do prawej krawędzi. Sekcja z kilkoma
   * niezależnie edytowalnymi wartościami nie ma gdzie postawić jednego
   * przycisku w nagłówku — „Zmień” musi stać przy danej, której dotyczy.
   */
  action?: React.ReactNode;
}

/** Wiersz danych: etykieta i wartość (`children`). */
const InfoCardRow = ({
  label,
  layout = 'spread',
  divided = false,
  action,
  className,
  children,
  ...props
}: InfoCardRowProps) =>
  layout === 'columns' ? (
    <div
      className={cn(
        'flex flex-row gap-2',
        divided && 'border-t border-neutral-100 pt-[11px]',
        className,
      )}
      {...props}
    >
      <div className="w-[120px] min-w-[120px] text-sm text-neutral-500">
        {label}
      </div>
      <div
        className={cn(
          // `break-words`, bo w kolumnie zostaje ~200 px, a wartością bywa
          // jednolity ciąg bez spacji (opis przelewu, numer rachunku).
          'min-w-0 flex-1 break-words text-sm font-medium text-base-text',
          action && 'flex items-center gap-3',
        )}
      >
        {action ? (
          <>
            <div className="min-w-0 flex-1">{children}</div>
            {action}
          </>
        ) : (
          children
        )}
      </div>
    </div>
  ) : (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 text-sm',
        divided && 'border-t border-neutral-100 pt-[11px]',
        className,
      )}
      {...props}
    >
      <span className="text-neutral-500">{label}</span>
      {action ? (
        <span className="flex items-center gap-3">
          {children}
          {action}
        </span>
      ) : (
        children
      )}
    </div>
  );

/**
 * Lista danych bez karty — treść sekcji (`SectionLayout`) albo modala, gdzie
 * własna ramka byłaby ramką w ramce.
 */
const InfoCardList = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('space-y-3', className)} {...props} />
);

export { InfoCard, InfoCardHeader, InfoCardBody, InfoCardRow, InfoCardList };
